import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Le visage qui réagit — RG-2.40, 06 §RG-D22.
 *
 * **Il commente le geste, jamais la personne.** « Celle-là est difficile »
 * plutôt que « tu es faible en botanique ». Un candidat se trompe des dizaines
 * de fois par séance ; un compagnon qui le jugerait à chaque erreur deviendrait
 * insupportable en dix minutes, et il fermerait l'application.
 *
 * **Il ne ment jamais sur la performance.** Féliciter une série d'échecs
 * détruit la confiance qu'on a dans tout ce qu'il dit ensuite — y compris ses
 * conseils. Quand ça va mal, il le reconnaît et propose autre chose.
 *
 * **Les expressions sont dessinées, pas écrites.** Un emoji changerait de tête
 * d'un système à l'autre et ne suivrait pas le thème.
 *
 * **Il vit dans l'EN-TÊTE** (RG-D22 amendée), à droite, et y remplace l'avatar
 * et le titre de l'écran. En pastille flottante, il recouvrait le bouton
 * « Réviser » sur les petits écrans — deux éléments flottants au même coin, et
 * le plus utile des deux caché. L'en-tête est un espace déjà réservé, visible
 * en permanence, que le défilement ne quitte pas.
 *
 * **Sa position est dictée par son texte** (RG-D22bis). Il est collé à gauche
 * de sa phrase, et l'ensemble est calé à droite : à mesure que le texte
 * s'écrit, le groupe s'étend vers la gauche, donc le visage recule
 * visiblement — puis revient à sa place quand le message s'efface.
 *
 * **Le texte s'écrit, puis s'efface.** Il apparaît caractère par caractère —
 * quelqu'un qui parle, pas une pancarte —, reste le temps qu'on le lise, puis
 * disparaît au profit de trois points qui respirent. Un message figé en
 * permanence devient un meuble : on cesse de le lire au bout de dix cartes, et
 * le jour où il a quelque chose d'utile à dire, personne ne le voit.
 *
 * Le catalogue complet des messages est dans
 * `docs/guides/visage-messages.md`.
 */
export type Humeur = 'neutre' | 'content' | 'ravi' | 'pensif' | 'encourageant'

/**
 * Millisecondes par caractère — 12 ms ≈ 83 signes par seconde.
 *
 * À 22 ms, une phrase de soixante signes mettait une seconde et demie à
 * s'écrire : assez pour qu'on ait déjà répondu à la carte suivante, et pour
 * que le message arrive après ce qu'il commente.
 */
const FRAPPE_MS = 12

/** Ce que le message reste à l'écran une fois écrit, avant de s'effacer. */
const LECTURE_MS = 6000

/**
 * Ce qu'on dit quand le silence dure — RG-2.40.
 *
 * Ils tournent, pour que le visage ne répète pas la même phrase toutes les
 * quarante secondes. Aucun ne reproche : « vous avez arrêté » ferait
 * culpabiliser quelqu'un qui lit peut-être simplement l'énoncé.
 */
const RELANCES = [
  'On continue quand vous voulez.',
  'Une de plus ?',
  'Prenez le temps de lire, rien ne presse.',
  'Je suis là.',
]

/** Après ce silence, on propose une relance plutôt que trois points. */
const RELANCE_MS = 25_000

export function Visage({ humeur, message }: { humeur: Humeur; message: string }) {
  // Le clignement : ce qui sépare un dessin d'un compagnon. Il ne dit rien du
  // score, il dit seulement que quelqu'un est là.
  const [cligne, setCligne] = useState(false)

  useEffect(() => {
    const battre = setInterval(() => {
      setCligne(true)
      setTimeout(() => { setCligne(false); }, 160)
    }, 4200)

    return () => { clearInterval(battre); }
  }, [])

  return (
    <div
      // Dans le flux de l'en-tête, calé à droite. `justify-end` sur le parent
      // fait le reste : le groupe s'étend vers la gauche à mesure que le texte
      // s'écrit, et le visage recule avec lui (RG-D22bis).
      className="flex min-w-0 items-center gap-2"
    >
      <Tete humeur={humeur} cligne={cligne} />

      {/* **La `key` est le message.** Elle remonte le composant à chaque
          nouveau message : la frappe repart de zéro sans qu'aucun effet n'ait
          à remettre l'état à la main — un `setState` synchrone dans un effet
          déclenche une cascade de rendus, et React le reproche à juste titre. */}
      <Texte key={message} message={message} />
    </div>
  )
}

/**
 * Le message, écrit caractère par caractère puis effacé.
 *
 * **Trois états, et le passage de l'un à l'autre est ce qui donne vie.**
 * `frappe` pendant l'écriture, `pose` le temps de la lecture, `points` ensuite.
 * Un nouveau message remonte le composant et repart de zéro : c'est le geste du
 * candidat qui commande, pas le minuteur.
 */
function Texte({ message }: { message: string }) {
  const [affiche, setAffiche] = useState('')
  const [etat, setEtat] = useState<'frappe' | 'pose' | 'points'>('frappe')
  const [relance, setRelance] = useState<string | null>(null)

  useEffect(() => {
    let rang = 0

    const plume = setInterval(() => {
      rang += 1
      setAffiche(message.slice(0, rang))

      if (rang >= message.length) {
        clearInterval(plume)
        setEtat('pose')
      }
    }, FRAPPE_MS)

    return () => { clearInterval(plume); }
  }, [message])

  // Une fois posé, le message s'efface au profit des trois points.
  useEffect(() => {
    if (etat !== 'pose') return

    const effacer = setTimeout(() => { setEtat('points'); }, LECTURE_MS)

    return () => { clearTimeout(effacer); }
  }, [etat])

  // Le silence long : on relance, une fois, sans reprocher.
  useEffect(() => {
    if (etat !== 'points') return

    const dire = setTimeout(() => {
      // L'index tourne sur la longueur du message : deux messages différents
      // ne donnent pas la même relance, et le visage ne se répète pas.
      setRelance(RELANCES[message.length % RELANCES.length] ?? null)
    }, RELANCE_MS)

    return () => { clearTimeout(dire); }
  }, [etat, message])

  return (
    <p
      // `role="status"` : le lecteur d'écran annonce le changement sans couper
      // la lecture en cours. Il lit le message ENTIER via `aria-label`, pas la
      // frappe caractère par caractère — sans quoi il le relirait à chaque signe.
      role="status"
      aria-label={relance ?? message}
      // `truncate` : passé une certaine longueur le texte s'arrête plutôt que
      // de repousser le visage hors de l'écran. C'est la position de repli
      // dont parle RG-D22bis.
      className="truncate text-[13px] font-bold leading-tight"
    >
      {relance !== null ? (
        <span aria-hidden="true">{relance}</span>
      ) : etat === 'points' ? (
        <Points />
      ) : (
        <span aria-hidden="true">{affiche}</span>
      )}
    </p>
  )
}

/** Trois points qui respirent — la respiration d'un compagnon qui attend. */
function Points() {
  return (
    <span className="flex items-center gap-1 py-1" aria-hidden="true">
      {[0, 1, 2].map((rang) => (
        <span
          key={rang}
          className="block size-1.5 rounded-full"
          style={{
            background: 'var(--texte-2)',
            animation: 'upfac-respire 1.4s ease-in-out infinite',
            animationDelay: `${String(rang * 0.18)}s`,
          }}
        />
      ))}
    </span>
  )
}

/**
 * La tête, en SVG.
 *
 * Les yeux ne bougent pas d'une humeur à l'autre — seule la bouche change, et
 * les sourcils sur `pensif`. C'est le minimum qui se lise à 34 pixels, et le
 * maximum qui ne devienne pas un dessin animé.
 */
function Tete({ humeur, cligne }: { humeur: Humeur; cligne: boolean }) {
  const bouches: Record<Humeur, string> = {
    // Une ligne presque droite : ni content ni mécontent.
    neutre: 'M16 30 Q24 32 32 30',
    content: 'M16 28 Q24 35 32 28',
    ravi: 'M15 27 Q24 38 33 27',
    // Légèrement descendante — de la concentration, pas de la tristesse.
    pensif: 'M16 31 Q24 29 32 31',
    encourageant: 'M16 29 Q24 34 32 29',
  }

  return (
    <svg viewBox="0 0 48 48" width={34} height={34} aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="24" cy="24" r="21" fill="var(--surface-2)" />
      <circle cx="24" cy="24" r="21" fill="none" stroke="var(--accent-fort)" strokeWidth="2" />

      {/* Les yeux : deux traits quand ils clignent, deux points sinon. */}
      {cligne ? (
        <>
          <path d="M14 19h6" stroke="var(--texte-1)" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M28 19h6" stroke="var(--texte-1)" strokeWidth="2.5" strokeLinecap="round" />
        </>
      ) : (
        <>
          <circle cx="17" cy="19" r="2.6" fill="var(--texte-1)" />
          <circle cx="31" cy="19" r="2.6" fill="var(--texte-1)" />
        </>
      )}

      {humeur === 'pensif' && (
        <>
          <path d="M13 13.5 21 12" stroke="var(--texte-1)" strokeWidth="2" strokeLinecap="round" />
          <path d="M35 13.5 27 12" stroke="var(--texte-1)" strokeWidth="2" strokeLinecap="round" />
        </>
      )}

      <path
        d={bouches[humeur]}
        fill="none"
        stroke="var(--texte-1)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}


/**
 * Le canal qui fait remonter la réaction du Fil jusqu'à l'en-tête.
 *
 * **Un contexte, et non une prop qui traverse cinq composants.** Le visage est
 * calculé dans `Feed` — c'est lui qui voit les réponses — et s'affiche dans
 * `AppShell`, à trois niveaux au-dessus. Le faire remonter de parent en parent
 * demanderait de traverser `FeedScreen` et `App`, qui n'ont rien à en faire, et
 * chacun devrait redéclarer un type dont il ne se sert pas.
 */
export const CanalDuVisage = createContext<{
  reaction: { humeur: Humeur; message: string }
  poser: (r: { humeur: Humeur; message: string }) => void
  muet: boolean
  taire: (m: boolean) => void
}>({
  reaction: { humeur: 'neutre', message: 'On y va ?' },
  poser: () => undefined,
  muet: false,
  taire: () => undefined,
})

export function FournisseurDuVisage({ children }: { children: ReactNode }) {
  const [reaction, poser] = useState<{ humeur: Humeur; message: string }>({
    humeur: 'neutre',
    message: 'On y va ? Une question à la fois.',
  })

  // RG-D22ter — le visage se tait pendant une lecture et pendant une épreuve.
  const [muet, taire] = useState(false)

  // `useMemo` : sans lui, l'objet change à chaque rendu du parent et tous les
  // consommateurs se redessinent — y compris le Fil entier.
  const valeur = useMemo(() => ({ reaction, poser, muet, taire }), [reaction, muet])

  return <CanalDuVisage.Provider value={valeur}>{children}</CanalDuVisage.Provider>
}

/** Le visage lu depuis l'en-tête. */
export function VisageDeLEntete() {
  const { reaction, muet } = useContext(CanalDuVisage)

  if (muet) return null

  // **La hauteur de la bande vient d'ICI** — §108. `AppShell` la réservait par
  // un `min-h` posé dès que le prop existait ; or ce prop est cet élément-là,
  // toujours « défini », même quand le visage se tait. La bande gardait donc
  // ses 53 px pendant une épreuve, et l'en-tête collant du quiz se glissait
  // dessous.
  return (
    <span className="flex min-h-[53px] items-center">
      <Visage humeur={reaction.humeur} message={reaction.message} />
    </span>
  )
}

/**
 * Fait taire le visage tant qu'il est monté — RG-D22ter, [DECISIONS §46].
 *
 * **Un composant plutôt qu'un appel.** L'écran qui doit faire taire le visage
 * le déclare en montant celui-ci ; le rétablissement se fait au démontage, tout
 * seul, sans qu'aucun écran ait à penser à le rendre. Un `taire(false)` oublié
 * dans une branche de sortie — le retour, l'erreur, l'expiration du
 * chronomètre — laisserait le visage muet pour le reste de la séance.
 *
 * **Pourquoi le faire taire.** Le visage commente la performance. Pendant une
 * épreuve notée, tout commentaire est un indice : c'est RG-3.11quater étendue à
 * ce qui n'est pas un verdict explicite mais en tient lieu. Et pendant une
 * lecture il n'a rien à commenter — il n'y a pas de réponse, donc pas de
 * performance. Un personnage qui parle sans rien à dire cesse d'être écouté
 * quand il a quelque chose à dire.
 */
export function VisageEnSilence() {
  const { taire } = useContext(CanalDuVisage)

  useEffect(() => {
    taire(true)

    return () => {
      taire(false)
    }
  }, [taire])

  return null
}
